<script setup lang="ts">
import { useVModel } from "@vueuse/core"
import { computed } from "vue"
import { consentDateFin, dialogParagraphs } from "@constant"

type Props = {
    visible: boolean
    type: "pdl" | "pce"
    /**
     * Date de fin des autorisations, JJ/MM/AAAA (lue dans le dossier par
     * l'étape appelante ; repli sur la constante du module de textes).
     */
    dateFin?: string
    /** Date de début de contrat saisie (gaz), JJ/MM/AAAA. */
    dateDebut?: string | null
}

const emit = defineEmits(["update:visible"])

const props = withDefaults(defineProps<Props>(), {
    visible: false,
    type: "pdl",
    dateFin: undefined,
    dateDebut: null,
})

const modelVisible = useVModel(props, "visible", emit, {
    eventName: "update:visible",
})

/**
 * Rubriques alignées sur ce que nous demandons (DEV-2274, spécification 2.4).
 * Textes du module unique `consent-texts`, rendus par interpolation Vue :
 * plus aucun `v-html`.
 */
const getParagraphs = computed(() =>
    dialogParagraphs({
        type: props.type,
        fin: props.dateFin ?? consentDateFin(null),
        debut: props.dateDebut,
    })
)
</script>
<template>
    <BaseDialog
        v-model:visible="modelVisible"
        modal
        :style="{ width: '39rem' }"
        class="dialog-consent"
        :pt="{
            content: 'dialog-consent-content',
            header: 'dialog-consent-header',
        }"
    >
        <template #header>
            <div class="flex gap-2 mx-auto items-center py-4">
                <img
                    src="https://res.cloudinary.com/dcoutwkb1/image/upload/w_120,f_auto,q_auto/v1745852809/icon_light_bulb_ys4k4h.png"
                    alt="Icône d'ampoule"
                    loading="lazy"
                    class="w-6 h-6"
                />
                <h4 class="font-bold text-primary-400 text-center">
                    En savoir plus sur mes données
                </h4>
            </div>
        </template>
        <!-- Sur Facture-->
        <div class="flex flex-col gap-6 items-center pb-6">
            <div
                v-for="(paragraph, index) in getParagraphs"
                :key="index"
                class="flex flex-col gap-2"
            >
                <h6 class="text-primary-400 font-medium text-sm">
                    {{ paragraph.title }}
                </h6>
                <div
                    v-for="(block, blockIndex) in paragraph.blocks"
                    :key="blockIndex"
                    class="flex flex-col gap-2"
                >
                    <p class="text-primary-900 text-xs leading-5">
                        <ConsentSegments :segments="block.lead" />
                    </p>
                    <ul
                        v-if="block.items"
                        class="list-disc pl-6 leading-5 text-primary-900 text-xs"
                    >
                        <li v-for="item in block.items" :key="item">
                            {{ item }}
                        </li>
                    </ul>
                </div>
            </div>
        </div>
        <div class="flex align-center justify-center py-2">
            <BaseButton
                label="Quitter"
                class="!min-w-fit"
                @click="modelVisible = false"
            />
        </div>
    </BaseDialog>
</template>

<style>
.dialog-consent-header {
    --p-dialog-header-padding: 24px 36px;
}
.dialog-consent-content {
    --p-dialog-content-padding: 0 36px 24px 36px;
}
</style>
